import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { createTemplateOutput, createTemplatePayloadUpdate } from '@/tools/models/templateStore.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type {
  DerivedFeatureWithColumnDefinition,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateOutput,
  TemplatePayload,
} from '@/tools/models/template.types';
import type { BaseToolPersistedState } from '@/tools/tool.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export interface ToolTemplateState extends BaseToolPersistedState {
  /** Stable container GUID — the canonical identity of the template across versions. */
  id: string;
  /** GUID of the specific template version currently loaded (draft or initial unlocked template). */
  versionId: string;
  /** Human-readable description of the template. */
  description: string;
  /** Variable inputs the user maps before applying the template. Includes the instance-name synthetic input. */
  inputFeatures: TemplateInput[];
  /**
   * Output features computed by the template. The union reflects different representations used across
   * the tool (apply), edit, and scaling-table contexts; narrow to the expected type at the call site.
   */
  derivedFeatures: TemplateOutput[] | ColumnDefinition[] | DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[];
  /** Display name of the template. */
  name: string;
  /** Workbook ID this template is scoped to, or undefined for shared/global templates. */
  scopedTo: string | undefined;
  /** Datasource ID identifying which template library this template belongs to. */
  datasourceId: string | undefined;
  /** ID of the feature currently open in the feature-edit panel; undefined when no panel is open. */
  featureToEditId: string | undefined;
  /** ID of the instance being re-applied or edited; undefined on the new-application flow. */
  instanceToEditId: string | undefined;
  /** Derived features that already exist on the saved template version (used to diff against edits). */
  existingDerivedFeatures: TemplateDerivedFeature[];
  /** Input features from the saved template version, excluding the synthetic instance-name feature. */
  existingInputFeatures: TemplateInput[];
  /** Derived features added in the current editing session but not yet persisted. */
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  /** Input features added in the current editing session but not yet persisted. */
  newInputFeatures: NewlyAddedInputFeature[];
  /** The synthetic SEEQ_MODEL_INSTANCE_NAME input feature, kept separate so it is never lost during template updates. */
  instanceNameFeature: TemplateInput | undefined;
  /** Whether the loaded template version is locked (immutable). */
  isLocked: boolean;
  /** Whether the loaded template is a draft. */
  isDraft: boolean;
  /** Production version number this draft was created from. */
  sourceVersionNumber: number | undefined;
  /** Canonical feature IDs from the version this draft was created from. Undefined when not loaded. */
  editBaseFeatureCanonicalIds: string[] | undefined;
  /** Total number of instances for this template across all versions. */
  instanceCount: number | undefined;
}

export type ToolTemplateDehydratedState = ToolTemplateState;

export interface ToolTemplateActions {
  reset: () => void;
  setTemplate: (template: TemplatePayload) => void;
  setFeatureToEditId: (featureToEditId: string | undefined) => void;
  setInstanceToEditId: (instanceToEditId: string | undefined) => void;
  setTemplateOutput: (templateOutput: TemplateOutputV1) => void;
  setVersionState: (params: {
    isLocked: boolean;
    isDraft: boolean;
    sourceVersionNumber?: number;
    editBaseFeatureCanonicalIds: string[] | undefined;
  }) => void;
  setInstanceCount: (instanceCount: number | undefined) => void;
  rehydrateForEdit: (payload: { type: string; modelData: TemplateOutputV1; instanceId: string }) => void;
}

export const initialToolTemplateState: ToolTemplateState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  id: '',
  versionId: '',
  description: '',
  inputFeatures: [],
  derivedFeatures: [],
  name: '',
  scopedTo: undefined,
  datasourceId: undefined,
  featureToEditId: undefined,
  instanceToEditId: undefined,
  existingDerivedFeatures: [],
  existingInputFeatures: [],
  newDerivedFeatures: [],
  newInputFeatures: [],
  instanceNameFeature: undefined,
  isLocked: false,
  isDraft: false,
  sourceVersionNumber: undefined,
  editBaseFeatureCanonicalIds: undefined,
  instanceCount: undefined,
});

const toolTemplateActionsFactory: ActionsFactory<ToolTemplateState, ToolTemplateActions> = (set, _get, resetState) => ({
  reset: resetState,

  setTemplate: (template: TemplatePayload) =>
    set((draft: ToolTemplateState) => {
      Object.assign(draft, createTemplatePayloadUpdate(template, draft.inputFeatures));
    }),

  setFeatureToEditId: (featureToEditId: string | undefined) =>
    set((draft: ToolTemplateState) => {
      draft.featureToEditId = featureToEditId;
    }),

  setInstanceToEditId: (instanceToEditId: string | undefined) =>
    set((draft: ToolTemplateState) => {
      draft.instanceToEditId = instanceToEditId;
    }),

  /** Sets template fields from an API TemplateOutputV1 response. */
  setTemplateOutput: (templateOutput: TemplateOutputV1) =>
    set((draft: ToolTemplateState) => {
      const {
        description,
        name,
        id,
        inputFeatures,
        derivedFeatures,
        scopedTo,
        versionId,
        datasourceId,
        baseVersionId,
      } = templateOutput;
      draft.name = name;
      draft.id = id;
      draft.versionId = versionId ?? '';
      draft.description = description ?? '';
      draft.inputFeatures = (inputFeatures ?? []) as TemplateInput[];
      draft.derivedFeatures = derivedFeatures ?? [];
      draft.scopedTo = scopedTo;
      draft.datasourceId = datasourceId;
      draft.existingDerivedFeatures = derivedFeatures ?? [];
      draft.existingInputFeatures = (inputFeatures?.filter(
        (feature) => feature.name !== SeeqNames.Models.InstanceNameInputFeatureName,
      ) ?? []) as TemplateInput[];
      draft.instanceNameFeature = inputFeatures?.find(
        (input) => input.name === SeeqNames.Models.InstanceNameInputFeatureName,
      ) as TemplateInput | undefined;
      draft.newInputFeatures = [];
      draft.newDerivedFeatures = [];
      draft.isDraft = !!baseVersionId;
    }),

  setVersionState: ({ isLocked, isDraft, sourceVersionNumber, editBaseFeatureCanonicalIds }) =>
    set((draft: ToolTemplateState) => {
      draft.isLocked = isLocked;
      draft.isDraft = isDraft;
      draft.sourceVersionNumber = sourceVersionNumber;
      draft.editBaseFeatureCanonicalIds = editBaseFeatureCanonicalIds;
    }),

  setInstanceCount: (instanceCount: number | undefined) =>
    set((draft: ToolTemplateState) => {
      draft.instanceCount = instanceCount;
    }),

  /** Populates the store from the MODEL_TOOL_REHYDRATE_FOR_EDIT flow (apply-template editing). */
  rehydrateForEdit: (payload: { type: string; modelData: TemplateOutputV1; instanceId: string }) =>
    set((draft: ToolTemplateState) => {
      Object.assign(draft, createTemplateOutput(payload.modelData));
      draft.instanceToEditId = payload.instanceId;
    }),
});

const [ToolTemplateStoreProvider, useToolTemplateStoreInstance, useToolTemplateSelector] = createStore(
  'ToolTemplate',
  initialToolTemplateState,
  toolTemplateActionsFactory,
);

export { ToolTemplateStoreProvider };

export const dehydrateToolTemplateState = (state: ToolTemplateState): ToolTemplateDehydratedState => state;

// Selector hooks
export const useToolTemplateId = () => useToolTemplateSelector((state) => state.id);
export const useToolTemplateVersionId = () => useToolTemplateSelector((state) => state.versionId);
export const useToolTemplateName = () => useToolTemplateSelector((state) => state.name);
export const useToolTemplateDescription = () => useToolTemplateSelector((state) => state.description);
export const useToolTemplateScopedTo = () => useToolTemplateSelector((state) => state.scopedTo);
export const useToolTemplateDatasourceId = () => useToolTemplateSelector((state) => state.datasourceId);
export const useToolTemplateFeatureToEditId = () => useToolTemplateSelector((state) => state.featureToEditId);
export const useToolTemplateInstanceToEditId = () => useToolTemplateSelector((state) => state.instanceToEditId);
export const useToolTemplateIsLocked = () => useToolTemplateSelector((state) => state.isLocked);
export const useToolTemplateIsDraft = () => useToolTemplateSelector((state) => state.isDraft);
export const useToolTemplateSourceVersionNumber = () => useToolTemplateSelector((state) => state.sourceVersionNumber);
export const useToolTemplateEditBaseFeatureCanonicalIds = () =>
  useToolTemplateSelector((state) => state.editBaseFeatureCanonicalIds);
export const useToolTemplateInstanceCount = () => useToolTemplateSelector((state) => state.instanceCount);
export const useToolTemplateInstanceNameFeature = () => useToolTemplateSelector((state) => state.instanceNameFeature);

// Derived selectors (arrays — must use reselect to avoid spurious re-renders on every store change)
export const useToolTemplateInputFeatures = () => useToolTemplateSelector((s: ToolTemplateState) => s.inputFeatures);

export const useToolTemplateDerivedFeatures = () =>
  useToolTemplateSelector((s: ToolTemplateState) => s.derivedFeatures);

export const useToolTemplateExistingDerivedFeatures = () =>
  useToolTemplateSelector((s: ToolTemplateState) => s.existingDerivedFeatures);

export const useToolTemplateExistingInputFeatures = () =>
  useToolTemplateSelector((s: ToolTemplateState) => s.existingInputFeatures);

export const useToolTemplateNewDerivedFeatures = () =>
  useToolTemplateSelector((s: ToolTemplateState) => s.newDerivedFeatures);

export const useToolTemplateNewInputFeatures = () =>
  useToolTemplateSelector((s: ToolTemplateState) => s.newInputFeatures);

export const useToolTemplateActions = () => useToolTemplateStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestToolTemplateStore(overrides?: Partial<ToolTemplateState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestToolTemplateStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialToolTemplateState, ...overrides }, toolTemplateActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateToolTemplateStore() {
  return createStoreInstance<ToolTemplateState, ToolTemplateActions>(
    initialToolTemplateState,
    toolTemplateActionsFactory,
  );
}

export const usePersistToolTemplateStore = () => {
  const register = useRegisterPersistedStore();
  const store = useToolTemplateStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqToolTemplateStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateToolTemplateState,
    });
  }, [register, store]);
};
